/*
 * Thin fetch wrapper. Always same-origin (relative paths), always sends the
 * HttpOnly session cookie via `credentials: 'include'`.
 *
 * 401 handling: every non-preview 401 from any endpoint triggers the shared
 * session-redirect (auth/session.ts::redirectToLogin), which navigates the
 * whole page to Keycloak with a returnUrl back to the current view. That
 * covers the "operator was mid-workflow when the session cookie expired"
 * case — before this hook was added, the API just threw ApiError and the
 * user saw random per-page error toasts with no way back into the app
 * except a manual F5.
 *
 * The redirect helper is idempotent so a burst of parallel-failing requests
 * (dashboard reload, hub reconnect, etc.) collapses into one navigation.
 * Callers still get an ApiError thrown so their `finally` blocks run and
 * their promises resolve — the browser navigation happens asynchronously.
 *
 * If a caller ever needs to inspect a 401 without triggering the redirect
 * (bootstrapSession did this before), it should either call `fetch` directly
 * or pass `{ allowUnauthorized: true }`.
 */

import { redirectToLogin, webHmiExpiredSignal } from '../auth/session';
import type { paths } from '../generated/api';

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

interface RequestInit {
  body?: unknown;
  signal?: AbortSignal;
  /** Opt out of the auto-redirect-to-login on 401. bootstrapSession uses this
   *  because it wants to distinguish the initial "no session yet" case from
   *  a session-expired mid-use case (both are 401, but the routing decision
   *  is the same — the flag exists so the throw path is deterministic). */
  allowUnauthorized?: boolean;
}

async function request<T>(
  method: string,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  let body: BodyInit | undefined;
  if (init?.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(init.body);
  }

  const response = await fetch(path, {
    method,
    headers,
    body,
    credentials: 'include',
    signal: init?.signal,
  });

  if (!response.ok) {
    let parsed: unknown = null;
    try {
      parsed = await response.json();
    } catch {
      // ignore — body may be empty or non-JSON
    }
    // Session expired mid-use → punt to Keycloak. Idempotent, so parallel
    // requests all hitting 401 in the same tick collapse into one navigation.
    const expired = response.status === 401
      && (parsed as { reason?: string } | null)?.reason === 'webhmi_session_expired';
    if (expired) {
      webHmiExpiredSignal.value = true;
    } else if (response.status === 401 && !init?.allowUnauthorized) {
      redirectToLogin();
    }
    throw new ApiError(response.status, `${method} ${path} -> ${response.status}`, parsed);
  }

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    return (await response.json()) as T;
  }
  return (await response.text()) as unknown as T;
}

async function requestForm<T>(path: string, form: FormData, signal?: AbortSignal): Promise<T> {
  // Multipart uploads: browser sets Content-Type + boundary itself when the
  // body is a FormData. Same cookie / 401 handling as the JSON path.
  const response = await fetch(path, {
    method: 'POST',
    headers: { Accept: 'application/json' },
    body: form,
    credentials: 'include',
    signal,
  });
  if (!response.ok) {
    let parsed: unknown = null;
    try { parsed = await response.json(); } catch { /* body may be non-JSON */ }
    if (response.status === 401) redirectToLogin();
    throw new ApiError(response.status, `POST ${path} -> ${response.status}`, parsed);
  }
  if (response.status === 204) return undefined as T;
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) return (await response.json()) as T;
  return (await response.text()) as unknown as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>('GET', path, { signal }),
  post: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>('POST', path, { body, signal }),
  postForm: <T>(path: string, form: FormData, signal?: AbortSignal) =>
    requestForm<T>(path, form, signal),
  put: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>('PUT', path, { body, signal }),
  patch: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>('PATCH', path, { body, signal }),
  delete: <T>(path: string, signal?: AbortSignal) => request<T>('DELETE', path, { signal }),
};

/** Convenience alias: the `paths` type from openapi-typescript is what typed
 *  callers should reference for parameter/response shapes. Re-exported so
 *  consumers don't need to know the generated file location. */
export type ApiPaths = paths;
